import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Eye, Lock, Database, Bell, UserCheck, Mail } from 'lucide-react';

const sections = [
  {
    icon: Database,
    title: 'Information We Collect',
    content: [
      'Account Information: Name, email address, password (encrypted), class/grade level, and role (student/parent/teacher).',
      'Usage Data: Chapter progress, quiz scores, study time, and feature usage to personalize your experience.',
      'Device Information: Basic device type and browser information for app optimization.',
      'Optional: Parent-child account linking information for family features.',
    ]
  },
  {
    icon: Eye,
    title: 'How We Use Your Information',
    content: [
      'Personalization: To generate AI study content tailored to your class and subjects.',
      'Progress Tracking: To show your learning progress to you and linked accounts (parents/teachers).',
      'Communication: To send study reminders, exam alerts, and important updates.',
      'Improvement: To analyze usage patterns and improve our AI algorithms.',
    ]
  },
  {
    icon: Lock,
    title: 'Data Security',
    content: [
      'All passwords are encrypted using industry-standard bcrypt hashing.',
      'Data is transmitted over HTTPS with TLS encryption.',
      'We use secure cloud infrastructure with regular security audits.',
      'Access to personal data is restricted to authorized personnel only.',
    ]
  },
  {
    icon: UserCheck,
    title: 'Your Rights',
    content: [
      'Access: You can view all your personal data through your profile settings.',
      'Correction: Update your information anytime through the app.',
      'Deletion: Request complete deletion of your account and associated data.',
      'Portability: Export your progress data in a standard format.',
    ]
  },
  {
    icon: Bell,
    title: 'Notifications & Communications',
    content: [
      'Email notifications for important account updates and security alerts.',
      'In-app notifications for study reminders and achievements.',
      'WhatsApp notifications (coming soon) - opt-in only.',
      'You can manage notification preferences in your profile settings.',
    ]
  },
];

export default function PrivacyPolicyPage({ onBack }) {
  return (
    <div className="min-h-screen bg-[#0f172a] pb-16">
      {/* Header */}
      <div className="px-4 pt-6 pb-4 flex items-center gap-3">
        <button 
          data-testid="back-from-privacy" 
          onClick={onBack} 
          className="text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-bold text-white font-['Outfit']">Privacy Policy</h1>
      </div>

      <div className="px-4 max-w-2xl mx-auto space-y-6">
        {/* Intro */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <Shield className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Your Privacy Matters</h2>
              <p className="text-xs text-slate-500">Last updated: February 2026</p>
            </div>
          </div>
          <p className="text-sm text-slate-400">
            At StudyClaw, we are committed to protecting your privacy and ensuring the security of your personal information. 
            This policy explains how we collect, use, and safeguard your data when you use our AI-powered learning platform.
          </p>
        </motion.div>

        {/* Sections */}
        {sections.map((section, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: (i + 1) * 0.1 }}
            className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6"
          >
            <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
              <section.icon className="w-5 h-5 text-violet-400" />
              {section.title}
            </h3>
            <ul className="space-y-3">
              {section.content.map((item, j) => (
                <li key={j} className="text-sm text-slate-400 flex items-start gap-2">
                  <span className="text-violet-400 mt-1">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        ))}

        {/* Children's Privacy */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-6"
        >
          <h3 className="text-base font-bold text-amber-400 mb-3">Children's Privacy</h3>
          <p className="text-sm text-slate-400 mb-3">
            StudyClaw is designed for students aged 10 and above (Classes 6-12). We take extra care to protect younger users:
          </p>
          <ul className="space-y-2 text-sm text-slate-400">
            <li className="flex items-start gap-2">
              <span className="text-amber-400 mt-1">•</span>
              <span>We recommend parental supervision for users under 13.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-400 mt-1">•</span>
              <span>Parents can link accounts to monitor their child's activity.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-400 mt-1">•</span>
              <span>We do not collect unnecessary personal information from minors.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-400 mt-1">•</span>
              <span>Group study features include safety guidelines and moderation.</span>
            </li>
          </ul>
        </motion.div>

        {/* Third-Party Services */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6"
        >
          <h3 className="text-base font-bold text-white mb-3">Third-Party Services</h3>
          <p className="text-sm text-slate-400 mb-3">
            We use the following third-party services to provide our features:
          </p>
          <ul className="space-y-2 text-sm text-slate-400">
            <li className="flex items-start gap-2">
              <span className="text-violet-400 mt-1">•</span>
              <span><strong className="text-slate-300">AI Services:</strong> OpenAI for content generation (no personal data shared)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-violet-400 mt-1">•</span>
              <span><strong className="text-slate-300">Payment Processing:</strong> Razorpay for secure payment handling</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-violet-400 mt-1">•</span>
              <span><strong className="text-slate-300">Cloud Storage:</strong> Secure cloud infrastructure for data storage</span>
            </li>
          </ul>
        </motion.div>

        {/* Contact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-gradient-to-br from-violet-600/20 to-cyan-600/20 border border-violet-500/30 rounded-2xl p-6 text-center"
        >
          <Mail className="w-8 h-8 text-violet-400 mx-auto mb-3" />
          <h3 className="text-base font-bold text-white mb-2">Questions About Privacy?</h3>
          <p className="text-sm text-slate-400 mb-3">
            If you have any questions or concerns about this policy, please contact us:
          </p>
          <a 
            href="mailto:privacy@studyclaw.io" 
            className="text-violet-400 hover:text-violet-300 text-sm font-medium"
          >
            privacy@studyclaw.io
          </a>
        </motion.div>

        {/* Footer */}
        <p className="text-center text-xs text-slate-600">
          This privacy policy is effective as of February 1, 2026.
        </p>
      </div>
    </div>
  );
}
